import "../styles/CheckoutView.css";
import { FiMinus, FiPlus } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../services/amplitude";

export default function CheckoutView({
  cartItems,
  total,
  onCheckout,
  onDecrementItem,
  onIncrementItem,
  onRemoveItem,
  onBack,
  loading = false,
}) {
  const { t, i18n } = useTranslation("checkout");

  function formatMoney(n) {
    const v = Number(n || 0);
    const locale = i18n.language?.startsWith("uk") ? "uk-UA" : "en-US";

    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v);

    return `${formatted} ${t("currency_symbol")}`;
  }

  function handleIncrement(it) {
    trackEvent("cart_item_added", {
      product_id: it?.id || null,
      product_name: it?.name || null,
      price: Number(it?.price ?? 0),
      qty: Number(it?.qty || 1) + 1,
      source: "checkout_view",
    });

    onIncrementItem?.(it);
  }

  function handleDecrement(it) {
    const qty = Number(it?.qty || 1);

    if (qty <= 1) {
      trackEvent("cart_item_removed", {
        product_id: it?.id || null,
        product_name: it?.name || null,
        price: Number(it?.price ?? 0),
        qty: 0,
        source: "checkout_view",
      });

      if (onRemoveItem) {
        onRemoveItem(it.id);
      } else {
        onDecrementItem?.(it.id);
      }

      return;
    }

    trackEvent("cart_item_removed", {
      product_id: it?.id || null,
      product_name: it?.name || null,
      price: Number(it?.price ?? 0),
      qty: qty - 1,
      source: "checkout_view",
    });

    onDecrementItem?.(it.id);
  }

  return (
    <div className="checkoutSheet">
      {loading ? (
        <div className="checkoutLoadingOverlay" role="status" aria-live="polite">
          <div className="checkoutLoadingCard">
            <div className="checkoutSpinner" />
            <div className="checkoutLoadingTitle">{t("loading_title")}</div>
            <div className="checkoutLoadingSub">{t("loading_sub")}</div>
          </div>
        </div>
      ) : null}

      {onBack ? (
        <button
          type="button"
          className="checkoutBackBtn"
          onClick={onBack}
          disabled={loading}
        >
          {t("back")}
        </button>
      ) : null}

      <div className="checkoutScroll">
        {cartItems.length === 0 ? (
          <div className="empty">{t("empty")}</div>
        ) : (
          <div className="items">
            {cartItems.map((it) => {
              const imgSrc = it.image_url || it.image_proxy_url || "";
              const title = it.name || t("fallback_product");
              const qty = Number(it.qty || 1);
              const price = Number(it.price ?? 0);

              return (
                <div key={it.id} className="itemCard">
                  <div className="itemCard__img">
                    {imgSrc ? (
                      <img
                        src={imgSrc}
                        alt={title}
                        className="itemCard__imgTag"
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : null}
                  </div>

                  <div className="itemCard__body">
                    <div className="itemCard__title fs-16 fw-extrabold upper" title={title}>
                      {title}
                    </div>

                    <div className="itemCard__bottom">
                      <div className="qty">
                        <button
                          type="button"
                          className="qtyBtn"
                          onClick={() => handleDecrement(it)}
                          disabled={loading}
                        >
                          <FiMinus />
                        </button>

                        <div className="qtyVal">{qty}</div>

                        <button
                          type="button"
                          className="qtyBtn"
                          onClick={() => handleIncrement(it)}
                          disabled={loading}
                        >
                          <FiPlus />
                        </button>
                      </div>

                      <div className="itemCard__price">{formatMoney(price)}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="checkoutFooter">
        <div className="checkoutFooterRow">
          <span className="checkoutFooterLabel">{t("total_label")}</span>
          <b className="checkoutFooterTotal">{formatMoney(total)}</b>
        </div>

        <button
          className="checkoutMainBtn"
          onClick={onCheckout}
          disabled={loading || cartItems.length === 0}
        >
          {loading ? t("wait") : t("checkout")}
        </button>
      </div>
    </div>
  );
}